import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ComponentLibraryTab = ({ config }) => {
  const [selectedCategory, setSelectedCategory] = useState('buttons');
  const [showCode, setShowCode] = useState({});

  const componentCategories = [
    { id: 'buttons', label: 'Buttons', icon: 'MousePointer' },
    { id: 'forms', label: 'Form Elements', icon: 'Edit' },
    { id: 'cards', label: 'Cards', icon: 'Square' },
    { id: 'navigation', label: 'Navigation', icon: 'Menu' },
    { id: 'feedback', label: 'Feedback', icon: 'MessageCircle' },
    { id: 'data', label: 'Data Display', icon: 'BarChart' }
  ];

  const toggleCode = (componentId) => {
    setShowCode(prev => ({
      ...prev,
      [componentId]: !prev[componentId]
    }));
  };

  const getThemeColors = () => ({
    primary: config?.colorPalette?.primary || '#2D9B9B',
    secondary: config?.colorPalette?.secondary || '#1A7A7A',
    accent: config?.colorPalette?.accent || '#3DB8B8',
    success: config?.colorPalette?.success || '#059669',
    warning: config?.colorPalette?.warning || '#d97706',
    error: config?.colorPalette?.error || '#dc2626'
  });

  const components = {
    buttons: [
      {
        id: 'primary-button',
        name: 'Primary Button',
        description: 'Main action button using primary brand colors',
        component: (
          <button 
            className="px-6 py-3 text-white rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg"
            style={{ 
              background: `linear-gradient(135deg, ${getThemeColors().primary} 0%, ${getThemeColors().secondary} 100%)`,
              fontFamily: config?.typography?.fontFamily?.body || 'Inter'
            }}
          >
            Primary Action
          </button>
        ),
        code: `<button className="px-6 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg">
  Primary Action
</button>`
      },
      {
        id: 'secondary-button',
        name: 'Secondary Button',
        description: 'Secondary action button with outline style',
        component: (
          <button 
            className="px-6 py-3 rounded-lg font-medium transition-all duration-200 border-2"
            style={{ 
              borderColor: getThemeColors().primary,
              color: getThemeColors().primary,
              fontFamily: config?.typography?.fontFamily?.body || 'Inter'
            }}
          >
            Secondary Action
          </button>
        ),
        code: `<button className="px-6 py-3 border-2 border-primary text-primary rounded-lg font-medium transition-all duration-200 hover:bg-primary hover:text-white">
  Secondary Action
</button>`
      },
      {
        id: 'icon-button',
        name: 'Icon Button',
        description: 'Button with icon for compact actions',
        component: (
          <button 
            className="p-3 rounded-lg transition-all duration-200 shadow-sm"
            style={{ 
              backgroundColor: getThemeColors().primary,
              color: 'white'
            }}
          >
            <Icon name="Plus" size={20} />
          </button>
        ),
        code: `<button className="p-3 bg-primary text-white rounded-lg transition-all duration-200 shadow-sm hover:shadow-md">
  <Icon name="Plus" size={20} />
</button>`
      }
    ],
    forms: [
      {
        id: 'text-input',
        name: 'Text Input',
        description: 'Standard text input field',
        component: (
          <div className="w-full max-w-md">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Full Name
            </label>
            <input
              type="text"
              placeholder="Enter your full name"
              className="w-full px-4 py-3 border border-secondary-300 rounded-lg transition-colors"
              style={{
                fontFamily: config?.typography?.fontFamily?.body || 'Inter',
                ':focus': {
                  borderColor: getThemeColors().primary,
                  ringColor: getThemeColors().primary
                }
              }}
            />
          </div>
        ),
        code: `<div>
  <label className="block text-sm font-medium text-text-primary mb-2">
    Full Name
  </label>
  <input
    type="text"
    placeholder="Enter your full name"
    className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
  />
</div>`
      },
      {
        id: 'select-input',
        name: 'Select Input',
        description: 'Dropdown selection input',
        component: (
          <div className="w-full max-w-md">
            <label className="block text-sm font-medium text-text-primary mb-2">
              User Role
            </label>
            <select
              className="w-full px-4 py-3 border border-secondary-300 rounded-lg transition-colors"
              style={{ fontFamily: config?.typography?.fontFamily?.body || 'Inter' }}
            >
              <option value="">Select role...</option>
              <option value="admin">Master Admin</option>
              <option value="clab">CLAB User</option>
              <option value="recruiter">Recruiter</option>
              <option value="employer">Employer</option>
            </select>
          </div>
        ),
        code: `<div>
  <label className="block text-sm font-medium text-text-primary mb-2">
    User Role
  </label>
  <select className="w-full px-4 py-3 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors">
    <option value="">Select role...</option>
    <option value="admin">Master Admin</option>
    <option value="clab">CLAB User</option>
    <option value="recruiter">Recruiter</option>
    <option value="employer">Employer</option>
  </select>
</div>`
      }
    ],
    cards: [
      {
        id: 'info-card',
        name: 'Information Card',
        description: 'Card for displaying key information',
        component: (
          <div className="bg-white border border-secondary-200 rounded-xl p-6 shadow-sm max-w-md">
            <div className="flex items-center space-x-3 mb-4">
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: getThemeColors().primary }}
              >
                <Icon name="Users" size={24} color="white" />
              </div>
              <div>
                <h3 className="font-semibold text-text-primary" style={{ fontFamily: config?.typography?.fontFamily?.heading || 'Inter' }}>
                  Total Users
                </h3>
                <p className="text-text-secondary text-sm">Active biodata records</p>
              </div>
            </div>
            <div className="flex items-end space-x-2">
              <span className="text-3xl font-bold" style={{ color: getThemeColors().primary }}>
                1,247
              </span>
              <span className="text-success text-sm font-medium mb-1">
                +12%
              </span>
            </div>
          </div>
        ),
        code: `<div className="bg-white border border-secondary-200 rounded-xl p-6 shadow-sm">
  <div className="flex items-center space-x-3 mb-4">
    <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
      <Icon name="Users" size={24} color="white" />
    </div>
    <div>
      <h3 className="font-semibold text-text-primary">Total Users</h3>
      <p className="text-text-secondary text-sm">Active biodata records</p>
    </div>
  </div>
  <div className="flex items-end space-x-2">
    <span className="text-3xl font-bold text-primary">1,247</span>
    <span className="text-success text-sm font-medium mb-1">+12%</span>
  </div>
</div>`
      },
      {
        id: 'action-card',
        name: 'Action Card',
        description: 'Card with action buttons',
        component: (
          <div className="bg-white border border-secondary-200 rounded-xl p-6 shadow-sm max-w-md">
            <h3 className="font-semibold text-text-primary mb-2" style={{ fontFamily: config?.typography?.fontFamily?.heading || 'Inter' }}>
              John Doe
            </h3>
            <p className="text-text-secondary text-sm mb-4">Construction Worker • ID: CLB-2024-001</p>
            <div className="flex space-x-3">
              <button 
                className="flex-1 px-4 py-2 text-white rounded-lg text-sm font-medium"
                style={{ backgroundColor: getThemeColors().success }}
              >
                Approve
              </button>
              <button 
                className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border"
                style={{ 
                  borderColor: getThemeColors().error,
                  color: getThemeColors().error
                }}
              >
                Reject
              </button>
            </div>
          </div>
        ),
        code: `<div className="bg-white border border-secondary-200 rounded-xl p-6 shadow-sm">
  <h3 className="font-semibold text-text-primary mb-2">John Doe</h3>
  <p className="text-text-secondary text-sm mb-4">Construction Worker • ID: CLB-2024-001</p>
  <div className="flex space-x-3">
    <button className="flex-1 px-4 py-2 bg-success text-white rounded-lg text-sm font-medium">
      Approve
    </button>
    <button className="flex-1 px-4 py-2 border border-error text-error rounded-lg text-sm font-medium">
      Reject
    </button>
  </div>
</div>`
      }
    ],
    navigation: [
      {
        id: 'breadcrumb',
        name: 'Breadcrumb Navigation',
        description: 'Navigation breadcrumb component',
        component: (
          <nav className="flex items-center space-x-2 text-sm">
            <span className="text-text-secondary">Dashboard</span>
            <Icon name="ChevronRight" size={16} className="text-text-muted" />
            <span className="text-text-secondary">User Management</span>
            <Icon name="ChevronRight" size={16} className="text-text-muted" />
            <span style={{ color: getThemeColors().primary }} className="font-medium">
              Add User
            </span>
          </nav>
        ),
        code: `<nav className="flex items-center space-x-2 text-sm">
  <span className="text-text-secondary">Dashboard</span>
  <Icon name="ChevronRight" size={16} className="text-text-muted" />
  <span className="text-text-secondary">User Management</span>
  <Icon name="ChevronRight" size={16} className="text-text-muted" />
  <span className="text-primary font-medium">Add User</span>
</nav>`
      },
      {
        id: 'tab-navigation',
        name: 'Tab Navigation',
        description: 'Tab navigation component',
        component: (
          <div className="border-b border-secondary-200">
            <nav className="flex space-x-8">
              {['Overview', 'Pending', 'Approved', 'Rejected'].map((tab, index) => (
                <button
                  key={tab}
                  className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                    index === 0 
                      ? 'border-current text-primary' :'border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                  style={index === 0 ? { borderColor: getThemeColors().primary, color: getThemeColors().primary } : {}}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>
        ),
        code: `<div className="border-b border-secondary-200">
  <nav className="flex space-x-8">
    <button className="py-2 px-1 border-b-2 border-primary text-primary font-medium text-sm">
      Overview
    </button>
    <button className="py-2 px-1 border-b-2 border-transparent text-text-secondary hover:text-text-primary font-medium text-sm">
      Pending
    </button>
  </nav>
</div>`
      }
    ],
    feedback: [
      {
        id: 'success-alert',
        name: 'Success Alert',
        description: 'Success notification alert',
        component: (
          <div className="bg-success-50 border border-success-200 rounded-lg p-4 max-w-md">
            <div className="flex items-center space-x-3">
              <Icon name="CheckCircle" size={20} className="text-success" />
              <div>
                <h4 className="font-medium text-success">Success</h4>
                <p className="text-success-700 text-sm">Biodata has been successfully approved.</p>
              </div>
            </div>
          </div>
        ),
        code: `<div className="bg-success-50 border border-success-200 rounded-lg p-4">
  <div className="flex items-center space-x-3">
    <Icon name="CheckCircle" size={20} className="text-success" />
    <div>
      <h4 className="font-medium text-success">Success</h4>
      <p className="text-success-700 text-sm">Biodata has been successfully approved.</p>
    </div>
  </div>
</div>`
      },
      {
        id: 'warning-alert',
        name: 'Warning Alert',
        description: 'Warning notification alert',
        component: (
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 max-w-md">
            <div className="flex items-center space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-warning" />
              <div>
                <h4 className="font-medium text-warning">Warning</h4>
                <p className="text-warning-700 text-sm">This action requires additional verification.</p>
              </div>
            </div>
          </div>
        ),
        code: `<div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
  <div className="flex items-center space-x-3">
    <Icon name="AlertTriangle" size={20} className="text-warning" />
    <div>
      <h4 className="font-medium text-warning">Warning</h4>
      <p className="text-warning-700 text-sm">This action requires additional verification.</p>
    </div>
  </div>
</div>`
      }
    ],
    data: [
      {
        id: 'stats-widget',
        name: 'Statistics Widget',
        description: 'Widget for displaying key statistics',
        component: (
          <div className="bg-white border border-secondary-200 rounded-xl p-6 shadow-sm max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-text-primary">Biodata Statistics</h3>
              <Icon name="TrendingUp" size={20} style={{ color: getThemeColors().success }} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: getThemeColors().primary }}>
                  156
                </div>
                <div className="text-sm text-text-secondary">Pending</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: getThemeColors().success }}>
                  892
                </div>
                <div className="text-sm text-text-secondary">Approved</div>
              </div>
            </div>
          </div>
        ),
        code: `<div className="bg-white border border-secondary-200 rounded-xl p-6 shadow-sm">
  <div className="flex items-center justify-between mb-4">
    <h3 className="font-semibold text-text-primary">Biodata Statistics</h3>
    <Icon name="TrendingUp" size={20} className="text-success" />
  </div>
  <div className="grid grid-cols-2 gap-4">
    <div className="text-center">
      <div className="text-2xl font-bold text-primary">156</div>
      <div className="text-sm text-text-secondary">Pending</div>
    </div>
    <div className="text-center">
      <div className="text-2xl font-bold text-success">892</div>
      <div className="text-sm text-text-secondary">Approved</div>
    </div>
  </div>
</div>`
      }
    ]
  };

  return (
    <div className="space-y-6">
      {/* Category Navigation */}
      <div className="border-b border-secondary-200">
        <nav className="flex space-x-1 overflow-x-auto pb-2">
          {componentCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 whitespace-nowrap ${
                selectedCategory === category.id
                  ? 'text-white shadow-md'
                  : 'text-text-secondary hover:bg-secondary-100 hover:text-text-primary'
              }`}
              style={selectedCategory === category.id ? {
                background: `linear-gradient(135deg, ${getThemeColors().primary} 0%, ${getThemeColors().secondary} 100%)`
              } : {}}
            >
              <Icon name={category.icon} size={16} />
              <span>{category.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Component Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {components[selectedCategory]?.map((component) => (
          <div key={component.id} className="bg-surface border border-secondary-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-text-primary mb-1">{component.name}</h3>
                <p className="text-sm text-text-secondary">{component.description}</p>
              </div>
              <button
                onClick={() => toggleCode(component.id)}
                className="p-2 text-text-secondary hover:text-text-primary rounded-lg hover:bg-secondary-100 transition-colors"
                title="View code"
              >
                <Icon name="Code" size={16} />
              </button>
            </div>

            {/* Component Preview */}
            <div className="mb-4 p-6 bg-gradient-to-br from-secondary-50 to-secondary-100 rounded-lg border border-secondary-200">
              <div className="flex items-center justify-center">
                {component.component}
              </div>
            </div>

            {/* Code Block */}
            {showCode[component.id] && (
              <div className="bg-secondary-900 rounded-lg p-4 overflow-x-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-secondary-400 font-medium">JSX</span>
                  <button
                    onClick={() => navigator.clipboard.writeText(component.code)}
                    className="text-xs text-secondary-400 hover:text-white transition-colors"
                  >
                    Copy
                  </button>
                </div>
                <pre className="text-sm text-secondary-200 overflow-x-auto">
                  <code>{component.code}</code>
                </pre>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Component Guidelines */}
      <div className="bg-gradient-to-r from-[#2D9B9B]/5 to-[#1A7A7A]/5 border border-[#2D9B9B]/20 rounded-xl p-6 mt-8">
        <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
          <Icon name="BookOpen" size={20} className="mr-2 text-[#2D9B9B]" />
          Component Guidelines
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <h4 className="font-medium text-text-primary mb-3">Consistency</h4>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li>• Use consistent spacing and sizing</li>
              <li>• Apply theme colors consistently</li>
              <li>• Maintain consistent interaction patterns</li>
              <li>• Follow established visual hierarchy</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-text-primary mb-3">Accessibility</h4>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li>• Ensure sufficient color contrast</li>
              <li>• Provide keyboard navigation</li>
              <li>• Include proper ARIA labels</li>
              <li>• Support screen readers</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-text-primary mb-3">Performance</h4>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li>• Optimize component rendering</li>
              <li>• Minimize re-renders</li>
              <li>• Use appropriate loading states</li>
              <li>• Implement proper error handling</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentLibraryTab;